import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Database, 
  Users, 
  Activity, 
  TrendingUp,
  Calendar,
  RefreshCw,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { useSharedPermissions } from '../../contexts/PermissionsContext';
import { Alert, AlertDescription } from '../ui/alert';

export function HubSpotDashboard() {
  const permissions = useSharedPermissions();
  const canManageHubSpot = permissions.canManageHubSpot || permissions.isAdmin;

  if (!canManageHubSpot) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">HubSpot Management</h1>
          <p className="text-muted-foreground">
            Overview of HubSpot integration and data synchronization
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                You do not have permission to access HubSpot management features. 
                Please contact your administrator to request the 'manage:hubspot' permission.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">HubSpot Management</h1>
        <p className="text-muted-foreground">
          Overview of HubSpot integration and data synchronization
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">
              Synced from HubSpot
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Sync</CardTitle>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">
              Automatic daily sync
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sync Status</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium">Active</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Integration healthy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Growth</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">
              New contacts this month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Integration Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Integration Status
          </CardTitle>
          <CardDescription>
            Current status of HubSpot integration and synchronization services
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <div className="font-medium">HubSpot API Connection</div>
                <div className="text-sm text-muted-foreground">Successfully connected to HubSpot</div>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
          </div>

          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-medium">Daily Sync Service</div>
                <div className="text-sm text-muted-foreground">Automatic synchronization every 24 hours</div>
              </div>
            </div>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200">Running</Badge>
          </div>

          <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5 text-yellow-600" />
              <div>
                <div className="font-medium">Database Storage</div>
                <div className="text-sm text-muted-foreground">Contact data stored in SQL database</div>
              </div>
            </div>
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Ready</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Available Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Available Actions
          </CardTitle>
          <CardDescription>
            Manage HubSpot data synchronization and backfill operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Database className="h-5 w-5 text-primary" />
                <div className="font-medium">Backfill Management</div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Start and monitor large-scale contact backfill operations with parallel processing
              </p>
              <div className="text-sm">
                <div className="text-primary font-medium">? Navigate to Backfilling</div>
              </div>
            </div>

            <div className="p-4 border rounded-lg opacity-60">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div className="font-medium text-muted-foreground">Contact Analytics</div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                View detailed analytics and insights about your HubSpot contacts
              </p>
              <div className="text-sm">
                <div className="text-muted-foreground">Coming Soon</div>
              </div>
            </div>

            <div className="p-4 border rounded-lg opacity-60">
              <div className="flex items-center gap-3 mb-2">
                <RefreshCw className="h-5 w-5 text-muted-foreground" />
                <div className="font-medium text-muted-foreground">Sync Configuration</div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Configure automatic synchronization settings and schedules
              </p>
              <div className="text-sm">
                <div className="text-muted-foreground">Coming Soon</div>
              </div>
            </div>

            <div className="p-4 border rounded-lg opacity-60">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                <div className="font-medium text-muted-foreground">Performance Metrics</div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Monitor sync performance, API usage, and data quality metrics
              </p>
              <div className="text-sm">
                <div className="text-muted-foreground">Coming Soon</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Recent Activity
          </CardTitle>
          <CardDescription>
            Latest synchronization and backfill operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recent activity</p>
            <p className="text-sm">Activity will appear here after running backfill operations</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}